import { z } from "zod";
import { localDateSchema, moneySchema, quantitySchema, uuidSchema } from "../common/primitives.js";
import { marketReportGroupingSchema } from "./enums.js";
/**
 * Market raporları (Prompt 16 aşama B). Tümü istasyon kapsamlıdır ve Excel'e aktarılabilir DÜZ satırlar döner
 * (kırılım anahtarı + ad + sayılar). Tanımlar (ADR 0016; ana oturum alan dokümanına işler):
 *
 *  · CİRO = kasa satış satırlarının KDV HARİÇ neti (`net − vat_amount`), yalnızca STOK İZLENEN ürünler (market,
 *    madeni yağ, kafe). İade ve tam iptal karşı kayıtları DEFTER TOPLAMI olarak girer: satırlar durumdan bağımsız
 *    toplanır (asıl + karşı kayıt = 0) — durum süzgeci iptali iki kez düşürür (pos.md "Defter toplamı").
 *  · SMM = aynı dönemin `SALE` / `SALE_RETURN` stok hareketlerinin değeri (işaret çevrilmiş). Brüt kâr = ciro − SMM.
 *  · Dönem = iş günü (`business_date`) aralığı, iki uç dâhil.
 */
const periodQuery = {
    companyId: uuidSchema,
    stationId: uuidSchema.optional(),
    from: localDateSchema,
    to: localDateSchema,
};
export const salesReportQuerySchema = z
    .object({ ...periodQuery, groupBy: marketReportGroupingSchema.default("STATION") })
    .refine((value) => value.from <= value.to, { path: ["to"], message: "Bitiş başlangıçtan önce olamaz" });
export const salesReportRowSchema = z.object({
    key: z.string().describe("Kırılım anahtarı (istasyon / kategori / ürün kimliği; kategorisiz: NONE)"),
    label: z.string(),
    code: z.string().nullable(),
    stationName: z.string().nullable().describe("Ürün kırılımında istasyon adı YOK (şirket toplamı)"),
    quantity: quantitySchema.describe("Net satılan miktar"),
    revenue: moneySchema.describe("KDV hariç net ciro (kuruş)"),
    cogs: moneySchema.describe("Satılan malın maliyeti (kuruş)"),
    grossProfit: moneySchema,
    marginPercent: z.string().nullable().describe("Brüt kâr / ciro × 100; ciro sıfırsa null"),
});
export const salesReportSchema = z.object({
    from: localDateSchema,
    to: localDateSchema,
    groupBy: marketReportGroupingSchema,
    rows: z.array(salesReportRowSchema),
    totals: salesReportRowSchema.omit({ key: true, label: true, code: true, stationName: true }),
});
export const periodReportQuerySchema = z
    .object(periodQuery)
    .refine((value) => value.from <= value.to, { path: ["to"], message: "Bitiş başlangıçtan önce olamaz" });
/**
 * Sepet ve dönüşüm (istasyon başına):
 *  · sepet ortalaması = geçerli (iptal EDİLMEMİŞ) market satış fişlerinin KDV hariç market neti / fiş sayısı —
 *    burada durum süzgeci DOĞRUDUR: soru "kaç geçerli fiş" dir (karşı kayıtlar fiş sayılmaz);
 *  · dönüşüm = akaryakıt satırı olan geçerli satış fişlerinden market (stok izlenen) satırı da olanların yüzdesi.
 */
export const basketReportRowSchema = z.object({
    stationId: uuidSchema,
    stationName: z.string(),
    saleCount: z.number().int().describe("Geçerli (iptal edilmemiş) satış fişi"),
    marketSaleCount: z.number().int().describe("En az bir market satırı olan geçerli fiş"),
    marketNet: moneySchema.describe("Bu fişlerin market satırlarının KDV hariç neti (kuruş)"),
    averageBasket: moneySchema.nullable().describe("marketNet / marketSaleCount (kuruş)"),
    fuelSaleCount: z.number().int().describe("Akaryakıt satırı olan geçerli fiş"),
    fuelWithMarketCount: z.number().int().describe("Akaryakıt + market satırı birlikte olan geçerli fiş"),
    conversionRatePercent: z.string().nullable().describe("fuelWithMarketCount / fuelSaleCount × 100"),
});
export const basketReportSchema = z.object({
    from: localDateSchema,
    to: localDateSchema,
    rows: z.array(basketReportRowSchema),
    totals: basketReportRowSchema.omit({ stationId: true, stationName: true }),
});
export const deadStockReportQuerySchema = z.object({
    companyId: uuidSchema,
    stationId: uuidSchema.optional(),
});
export const deadStockRowSchema = z.object({
    stationId: uuidSchema,
    stationName: z.string(),
    warehouseId: uuidSchema,
    warehouseCode: z.string(),
    productId: uuidSchema,
    productCode: z.string(),
    productName: z.string(),
    categoryName: z.string().nullable(),
    quantity: quantitySchema,
    value: moneySchema,
    lastSaleDate: localDateSchema.nullable().describe("Son satış (İstanbul günü); hiç satılmadıysa null"),
    daysSinceLastSale: z.number().int().nullable(),
    deadStockDays: z.number().int().describe("İstasyonun ölü stok eşiği (market ayarı)"),
});
export const deadStockReportSchema = z.object({
    asOf: localDateSchema,
    rows: z.array(deadStockRowSchema),
    totals: z.object({ lineCount: z.number().int(), value: moneySchema }),
});
/**
 * Stok devir hızı = dönem SMM / ortalama stok değeri; ortalama stok = (dönem başı + dönem sonu) / 2. Dönem başı ve
 * sonu stok HAREKET DEFTERİNDEN yeniden kurulur: değer(gün) = Σ hareket değeri (iş günü ≤ gün).
 */
export const turnoverReportQuerySchema = z
    .object({ ...periodQuery, groupBy: marketReportGroupingSchema.default("STATION") })
    .refine((value) => value.from <= value.to, { path: ["to"], message: "Bitiş başlangıçtan önce olamaz" });
export const turnoverRowSchema = z.object({
    key: z.string(),
    label: z.string(),
    code: z.string().nullable(),
    cogs: moneySchema,
    openingValue: moneySchema.describe("Dönem başı stok değeri (from gününden önceki hareketler)"),
    closingValue: moneySchema.describe("Dönem sonu stok değeri (to günü dâhil)"),
    averageValue: moneySchema,
    turnover: z.string().nullable(),
    daysOnHand: z.string().nullable().describe("Dönem günü / devir hızı"),
});
export const turnoverReportSchema = z.object({
    from: localDateSchema,
    to: localDateSchema,
    periodDays: z.number().int(),
    groupBy: marketReportGroupingSchema,
    rows: z.array(turnoverRowSchema),
    totals: turnoverRowSchema.omit({ key: true, label: true, code: true }),
});
export const expiringLotsReportQuerySchema = z.object({
    companyId: uuidSchema,
    stationId: uuidSchema.optional(),
    days: z.coerce
        .number()
        .int()
        .min(0)
        .max(365)
        .optional()
        .describe("Bu kadar gün içinde bitecekler (verilmezse istasyonun SKT uyarı günü)"),
    includeExpired: z.enum(["true", "false"]).optional().describe("SKT'si geçmişler de (varsayılan: evet)"),
});
export const expiringLotRowSchema = z.object({
    lotId: uuidSchema,
    stationId: uuidSchema,
    stationName: z.string(),
    warehouseCode: z.string(),
    productId: uuidSchema,
    productCode: z.string(),
    productName: z.string(),
    lotNo: z.string().nullable(),
    expiryDate: localDateSchema,
    daysToExpiry: z.number().int(),
    state: z.enum(["EXPIRED", "EXPIRING"]),
    quantity: quantitySchema,
    estimatedValue: moneySchema.describe("Kalan × ortalama birim maliyet (kuruş)"),
});
export const expiringLotsReportSchema = z.object({
    asOf: localDateSchema,
    rows: z.array(expiringLotRowSchema),
    totals: z.object({ lineCount: z.number().int(), quantity: quantitySchema, estimatedValue: moneySchema }),
});
//# sourceMappingURL=reports.js.map